/**
 * Normalizes a numeric value or string into a formatted decimal string with a specific number of decimal places.
 *
 * @param {number | string | undefined} value - The input value to normalize. Can be a number or a string.
 * @param {number} [minDecimalPlaces=2] - Minimum number of decimal places to ensure in the output.
 * @param {number} [maxDecimalPlaces=8] - Maximum number of decimal places to ensure in the output.
 * @param {boolean} [convertBlankToZero=false] - If true, converts blank or falsy values to "0,00".
 * @returns {string} The normalized decimal string with the required format.
 */

type NormalizeDecimalOptions = {
  minDecimalPlaces?: number
  maxDecimalPlaces?: number
  convertBlankToZero?: boolean
  thousandsSeparator?: string | undefined
}

const normalizeDecimalDefaults = {
  minDecimalPlaces: 2,
  maxDecimalPlaces: 8,
  convertBlankToZero: false,
  thousandsSeparator: undefined
}

export function normalizeDecimal(
  value: number | string | undefined | null,
  options: NormalizeDecimalOptions = {}
): string {
  // Merge provided options and default options
  const opts = { ...normalizeDecimalDefaults, ...options }

  // Handle empty or undefined values
  if (value === "" || value === undefined || value === null) {
    if (opts.convertBlankToZero) {
      value = 0 // Convert blank to zero if the flag is set
    } else {
      return "" // Return an empty string for falsy values
    }
  }

  // Convert the value to a number
  const numericValue = typeof value === "number" ? value : parseFloat(String(value))

  // Handle special cases for zero (including "0E-8")
  if (numericValue === 0) {
    return "0,00" // Ensure the output for zero matches the format
  }

  // Round the value properly using toFixed
  const fixedValue = numericValue.toFixed(opts.maxDecimalPlaces)

  // Convert the fixed value to a string and split into integer and decimal parts
  // eslint-disable-next-line prefer-const
  let [intPart, decPart = ""] = fixedValue.split(".")

  // Insert thousands separator if provided
  if (opts.thousandsSeparator) {
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, opts.thousandsSeparator)
  }

  // Trim trailing zeros from the decimal part
  let trimmedDecPart = decPart.replace(/0+$/, "")

  // Ensure the decimal part has at least the minimum number of decimal places
  if (trimmedDecPart.length < opts.minDecimalPlaces) {
    trimmedDecPart = trimmedDecPart + "0".repeat(opts.minDecimalPlaces - trimmedDecPart.length)
  }

  // Combine the integer and decimal parts, using a comma as the separator
  if (trimmedDecPart) {
    return `${intPart},${trimmedDecPart}`
  }

  return intPart
}

/**
 * Normalizes a numeric value or bigint into a formatted currency string.
 *
 * @param {number | bigint | undefined | null} value - The input value to normalize.
 * @returns {string} The normalized currency string with the format "€ 1.234,56".
 */
export function normalizeCurrency(value: number | bigint | undefined | null): string {
  if (value === undefined || value === null || (typeof value === "number" && Number.isNaN(value))) {
    return ""
  }
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(value)
}

/**
 * Normalizes a user-entered numeric value (string or number) to remove
 * floating-point noise while preserving the number of decimals the user entered.
 *
 * - Accepts both comma and dot as decimal separators (e.g., "1,23" or "1.23").
 * - Automatically determines the number of decimals from the user input.
 * - Rounds only to that precision (capped at 16 decimals for IEEE-754 safety).
 * - Converts the result back to a JavaScript Number with clean binary representation.
 *
 * Examples:
 * ```js
 * normalizeFloatInput("1")                  // → 1
 * normalizeFloatInput("1,1")                // → 1.1
 * normalizeFloatInput("1,1111111")          // → 1.1111111
 * normalizeFloatInput("1,11111111111111")   // → 1.11111111111111
 * normalizeFloatInput(1 - 0.77)             // → 0.23
 * normalizeFloatInput("0.22999999999999998")// → 0.23
 * ```
 *
 * @param {string | number} value - The user-entered value, possibly containing a comma or dot.
 * @returns {number} The normalized numeric value, rounded to the user’s original precision.
 */
export function normalizeFloat(value: number | string) {
  if (typeof value === "string") {
    // Replace comma with dot and trim
    value = value.replace(",", ".").trim()
  }

  const num = Number(value)
  if (!Number.isFinite(num)) return NaN

  // Determine number of decimals from input text
  const decimalsMatch = String(value).split(".")[1]
  const decimals = decimalsMatch ? decimalsMatch.length : 0

  // Round to that many decimals (but cap to 16 for safety)
  const safeDecimals = Math.min(decimals, 8)
  return Number(num.toFixed(safeDecimals))
}
